import type { DomainEventType } from "../taxonomy.js"
import { workflowActionEntry } from "./catalog.js"
import type { WorkflowActionType } from "./vocabulary.js"

/* The two anti-runaway checks, in one place because they are one idea at two
   scales: a rule that leads back to itself.

   Both run when a rule is saved and again when it runs. Belt and braces, and
   the braces matter: the API can be called directly, there is deliberately no
   daily run ceiling, and a loop whose every step succeeds never trips the
   consecutive-failure auto-pause that is otherwise the only backstop. */

export type WorkflowLoop = {
  readonly actionType: WorkflowActionType
  readonly eventType: DomainEventType
}

type LoopCandidate = {
  readonly triggerEventType: string
  readonly actions: readonly { readonly actionType: string }[]
}

/** The events one rule's actions can cause. */
function producedEventTypes(
  actions: readonly { readonly actionType: string }[]
): readonly DomainEventType[] {
  const produced = new Set<DomainEventType>()

  for (const action of actions) {
    for (const eventType of workflowActionEntry(action.actionType)
      ?.producesEventTypes ?? []) {
      produced.add(eventType)
    }
  }

  return [...produced]
}

/**
 * A rule whose trigger is the same kind of thing one of its own actions
 * produces. It would start itself, forever.
 */
export function detectWorkflowLoop(
  triggerEventType: string,
  actions: readonly { readonly actionType: string }[]
): WorkflowLoop | null {
  for (const action of actions) {
    const entry = workflowActionEntry(action.actionType)

    if (!entry) {
      continue
    }

    const produced = entry.producesEventTypes.find(
      (eventType) => eventType === triggerEventType
    )

    if (produced) {
      return { actionType: entry.value, eventType: produced }
    }
  }

  return null
}

export type WorkflowCycleMember = {
  readonly id: string
  readonly name: string
  readonly triggerEventType: string
}

export type WorkflowCycle = {
  /** The rules that close the ring, in the order they would fire. */
  readonly members: readonly WorkflowCycleMember[]
}

type CycleCandidate = LoopCandidate & {
  readonly id: string
  readonly name: string
}

/**
 * Whether a set of rules closes a ring, rather than a rule looping on itself.
 *
 * `detectWorkflowLoop` only ever sees one rule, and a ring of two is just as
 * unbounded and much easier to build by accident: "an export is ready, post it"
 * plus "a posting finished, build an export" never fails, so nothing stops it.
 *
 * Pure and whole-workspace: pass every active rule plus the one being saved.
 * The engine keeps a second, dumber guard for what no static check can see, a
 * customer webhook that calls the upload API and re-enters at the top.
 */
export function detectWorkflowCycle(
  workflows: readonly CycleCandidate[]
): WorkflowCycle | null {
  // Both sides of the edge are indexed, so the walk is over the graph rather
  // than a rescan of every rule for every event a node produces.
  const produces = new Map<string, readonly DomainEventType[]>()
  const triggeredBy = new Map<string, CycleCandidate[]>()
  const byId = new Map<string, CycleCandidate>()

  for (const workflow of workflows) {
    produces.set(workflow.id, producedEventTypes(workflow.actions))
    byId.set(workflow.id, workflow)

    const waiting = triggeredBy.get(workflow.triggerEventType)

    if (waiting) {
      waiting.push(workflow)
    } else {
      triggeredBy.set(workflow.triggerEventType, [workflow])
    }
  }

  const visiting = new Set<string>()
  const settled = new Set<string>()
  const path: string[] = []

  const walk = (id: string): WorkflowCycle | null => {
    if (visiting.has(id)) {
      const start = path.indexOf(id)

      return {
        members: path.slice(start === -1 ? 0 : start).map((memberId) => {
          const member = byId.get(memberId)

          return {
            id: memberId,
            name: member?.name ?? "",
            triggerEventType: member?.triggerEventType ?? "",
          }
        }),
      }
    }

    if (settled.has(id)) {
      return null
    }

    visiting.add(id)
    path.push(id)

    for (const eventType of produces.get(id) ?? []) {
      for (const next of triggeredBy.get(eventType) ?? []) {
        const cycle = walk(next.id)

        if (cycle) {
          return cycle
        }
      }
    }

    visiting.delete(id)
    settled.add(id)
    path.pop()

    return null
  }

  for (const workflow of workflows) {
    const cycle = walk(workflow.id)

    if (cycle) {
      return cycle
    }
  }

  return null
}

/**
 * How many workflow runs may descend from one original event.
 *
 * Five is not a tuning knob. A legitimate chain is short: an approval posts,
 * the posting finishes, a rule emails somebody about it. Nothing anybody has
 * described needs a fifth generation, and the number exists to stop something
 * nobody described.
 */
export const workflowMaxRunDepth = 5

/**
 * A ceiling on how long a chain of runs may get.
 *
 * What it covers, precisely: chains that run through a posting or an export
 * this product made. A run's depth is read back through the workflow_run_step
 * that points at the posting or the export the next event is about, so
 * `posting_completed` and `export_generated` carry it and the other four
 * triggers cannot. It bounds a long chain that the static checks let through
 * because it is not a cycle.
 *
 * What it does NOT cover, said plainly because an earlier version of this
 * comment claimed otherwise: a loop that leaves the building. A rule fires a
 * webhook, the customer's receiver calls the upload API, and a new document
 * arrives under their own credentials with nothing linking it to the run that
 * caused it. There is no join to make, and nothing we can put in the webhook
 * body survives a receiver that does not echo it. That chain re-enters as
 * `document_processed` at depth zero every lap.
 *
 * What actually bounds that case is the page budget, which stops a runaway at
 * the wallet, and the consecutive-failure auto-pause when the runs fail. A loop
 * of successful runs through a customer's own re-upload is bounded only by what
 * they are paying for pages, which is the decision recorded against "no daily
 * ceiling" rather than an oversight here.
 */
export function exceedsWorkflowRunDepth(depth: number): boolean {
  return depth >= workflowMaxRunDepth
}

/**
 * The depth an event caused by a run at `depth` should carry.
 *
 * Not exported from the package: it sits next to workflowRunDepthFrom and
 * taking the wrong one of the two is exactly the confusion that disabled the
 * guard once already. Callers outside this module want workflowRunDepthFrom.
 */
export function nextWorkflowRunDepth(depth: number | null | undefined): number {
  return Math.max(0, Math.floor(depth ?? 0)) + 1
}

/**
 * The depth to record for a run, given the depth of the run that made the thing
 * it is about.
 *
 * Null means nothing here was made by a workflow, so this is generation zero.
 * Anything else is a parent's own depth and this run is one deeper.
 *
 * One line, and its own function, because getting it wrong is silent: the
 * engine once collapsed "no parent" and "parent at depth zero" into the same
 * answer and then subtracted one before adding one. Every run was written at
 * zero, every lookup of a child returned zero, and the ceiling was never
 * reached. Nothing failed, no test went red, and the only backstop against a
 * loop that leaves the building did nothing at all.
 */
export function workflowRunDepthFrom(parentDepth: number | null): number {
  return parentDepth === null ? 0 : nextWorkflowRunDepth(parentDepth)
}
